import { IGeoImageRepository } from '../../domain/repositories/IGeoImage.repository'
import { GeoImageClient } from './GeoImageClient.abstract'
import { GeoImage } from '../../domain/entities/GeoImage'
import { GeoImagesByTypeDto } from '../../domain/dtos/GeoImagesByType.dto'
import { CreateGeoImageSchema } from '../schemas/CreateGeoImage.schema'
import { CreatedGeoImageIdSchema } from '../schemas/CreatedGeoImageId.schema'
import { formatZodIssues } from '../../../@shared/infra/schemas/formatZodIssues'
import { GeoImageSchema } from '../schemas/GeoImage.schema'
import { GeoImagesByTypeSchema } from '../schemas/GeoImagesByType.schema'
import { UpdateGeoImageSchema } from '../schemas/UpdateGeoImage.schema'

export class GeoImageRepositoryApiAdapter implements IGeoImageRepository {
	private readonly _endpoint = '/v2/ImagemParaProcessamento'

	constructor(private readonly _httpClient: GeoImageClient) {}

	/**
	 * Qualquer 2xx e sucesso: o POST de criacao responde 201 e o DELETE pode
	 * responder 204, entao comparar so com 200 rejeitava respostas validas.
	 */
	private _isSuccess(status: number): boolean {
		return status >= 200 && status < 300
	}

	async create(geoImage: GeoImage): Promise<string> {
		const parsed = CreateGeoImageSchema.safeParse(geoImage)

		if (!parsed.success) {
			throw new Error(`Dados inválidos para cadastro de imagem — ${formatZodIssues(parsed.error)}`)
		}

		const input = parsed.data

		// A API responde 200 com corpo vazio, sem o id do registro criado. O
		// snapshot anterior permite identificar depois qual registro surgiu, que e
		// o que torna possivel desfazer o cadastro se o usuario cancelar.
		const idsBefore = new Set((await this.getAll()).map((image) => image.id))

		const config = {
			headers: {
				'Content-Type': 'multipart/form-data; boundary=---011000010111000001101001',
			},
		}

		const response = await this._httpClient.post(this._endpoint, input, config)

		if (!this._isSuccess(response.status)) {
			throw new Error('Não foi possível cadastrar imagem. Por favor, tente novamente')
		}

		const fromBody = CreatedGeoImageIdSchema.safeParse(response.data)
		if (fromBody.success) return fromBody.data

		return this._findCreatedId(idsBefore)
	}

	/**
	 * Descobre o id do registro recem-criado comparando a listagem com o snapshot
	 * anterior ao POST. Se mais de um registro aparecer, alguem cadastrou em
	 * paralelo e nao da para afirmar qual e o nosso: melhor devolver vazio do que
	 * arriscar remover o registro de outra pessoa no cancelamento.
	 */
	private async _findCreatedId(idsBefore: Set<string | undefined>): Promise<string> {
		const appeared = (await this.getAll())
			.map((image) => image.id)
			.filter((id): id is string => Boolean(id) && !idsBefore.has(id))

		if (appeared.length === 1) return appeared[0]

		console.error(
			'[GeoImageRepository.create] nao foi possivel identificar o registro criado.',
			`novos=${appeared.length} ids=${JSON.stringify(appeared)}`,
		)
		return ''
	}

	async update(geoImage: GeoImage): Promise<void> {
		const parsed = UpdateGeoImageSchema.safeParse(geoImage)

		if (!parsed.success) {
			throw new Error(`Dados inválidos para edição de imagem — ${formatZodIssues(parsed.error)}`)
		}

		const input = parsed.data

		const config = {
			headers: {
				'Content-Type': 'multipart/form-data; boundary=---011000010111000001101001',
			},
		}

		const response = await this._httpClient.put(this._endpoint, input, config)

		if (!this._isSuccess(response.status)) {
			throw new Error('Não foi possível editar imagem. Por favor, tente novamente')
		}

		return
	}

	async delete(exclusionZoneIds: string[]): Promise<void> {
		const response = await this._httpClient.delete(this._endpoint, {
			params: { id: exclusionZoneIds },
			paramsSerializer: {
				indexes: null,
			},
		})

		if (!this._isSuccess(response.status)) {
			throw new Error('Não foi possível excluir imagem. Por favor, tente novamente')
		}

		return
	}

	async getAll(): Promise<GeoImage[]> {
		const response = await this._httpClient.get(`${this._endpoint}/recuperar-todas`)

		if (!this._isSuccess(response.status)) return []

		return GeoImageSchema.array().parse(response.data)
	}

	async getByMeasurementId(measurementId: string): Promise<GeoImagesByTypeDto> {
		const response = await this._httpClient.get(
			`${this._endpoint}/recuperar-para-dropdown-estrato-inventario-florestal-por-id-medicao/${measurementId}`,
		)

		if (!this._isSuccess(response.status)) return { drone: [], lidar: [], mdt: [], satelite: [] }

		return GeoImagesByTypeSchema.parse(response.data)
	}
}
